// SlidingWindowAnimation.js

import React, { useState } from "react";

/**
 * Generates the step-by-step process for a fixed-size sliding window.
 * In this example, we calculate the sum of every subarray of size k.
 */
function getSlidingWindowSteps(nums, k) {
  let steps = [];
  let windowSum = 0;
  let start = 0;

  // Iterate over each element
  for (let end = 0; end < nums.length; end++) {
    windowSum += nums[end]; // Add the current element

    // Once we've hit the size k, record the step and move the window
    if (end >= k - 1) {
      steps.push({
        start,
        end,
        sum: windowSum,
        message: `Window [${start}, ${end}] => sum = ${windowSum}`
      });

      // Subtract the element going out of the window and advance start
      windowSum -= nums[start];
      start++;
    }
  }

  return steps;
}

const SlidingWindowAnimation = () => {
  // Sample array and window size (customize as you wish)
  const nums = [2, 5, 1, 8, 2, 9, 1];
  const k = 3;

  // Generate the sequence of steps for the sliding window
  const steps = getSlidingWindowSteps(nums, k);

  // React state to track which step is currently displayed
  const [stepIndex, setStepIndex] = useState(0);

  const handlePrevStep = () => {
    if (stepIndex > 0) {
      setStepIndex(stepIndex - 1);
    }
  };

  const handleNextStep = () => {
    if (stepIndex < steps.length - 1) {
      setStepIndex(stepIndex + 1);
    }
  };

  // Grab the current step details
  const currentStep = steps[stepIndex];
  const { start, end, sum, message } = currentStep;

  return (
    <div className="p-4 bg-gray-100 dark:bg-gray-900">
      <h4 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
        Sliding Window Animation Sample
      </h4>

      {/* Display array with a highlight on the current window range */}
      <div className="flex space-x-4 mb-6">
        {nums.map((num, index) => {
          // Highlight indices within the current window [start, end]
          const isInWindow = index >= start && index <= end;
          const bgColor = isInWindow ? "bg-green-600" : "bg-gray-200";
          return (
            <div
              key={index}
              className={`w-12 h-12 flex items-center justify-center ${bgColor} rounded shadow`}
            >
              {num}
            </div>
          );
        })}
      </div>

      {/* Step navigation buttons */}
      <div className="mb-4 space-x-2">
        <button
          onClick={handlePrevStep}
          disabled={stepIndex === 0}
          className={`px-4 py-2 rounded text-white ${
            stepIndex > 0 ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400"
          }`}
        >
          Previous Step
        </button>
        <button
          onClick={handleNextStep}
          disabled={stepIndex === steps.length - 1}
          className={`px-4 py-2 rounded text-white ${
            stepIndex < steps.length - 1
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400"
          }`}
        >
          Next Step
        </button>
      </div>

      {/* Step details */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-md shadow">
        <p className="text-gray-900 dark:text-gray-200">
          <strong>Step {stepIndex + 1} / {steps.length}:</strong> {message}
        </p>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          <strong>start:</strong> {start}, <strong>end:</strong> {end}, 
          <strong> sum:</strong> {sum}
        </p>
      </div>
    </div>
  );
};

export default SlidingWindowAnimation;
